// Approval and Session, under Only when, on the products that have them
// (SSH; Database without recording or re-verify). Approval is hidden for
// Deny. Approver groups, reason and ticket set outside the console are kept
// and shown read-only with remove.
import React from "react";
import { InputNumber, Switch } from "antd";
import { X } from "lucide-react";

import { AN_COLORS } from "../../../design-system/tokens";
import { SET_OUTSIDE } from "../../../components/policyShell/signals";
import { Field, Group, Muted, Note } from "../ui";
import RemotePicker from "./RemotePicker";
import { fieldDomId } from "./draft";

// Which session controls a product has: features.session === true means all.
export const sessionControlsOf = (session) =>
  session === true ? { record: true, reauth: true } : { record: !!session?.record, reauth: !!session?.reauth };

export const APPROVAL_DESC =
  "After the user's own MFA, every approver gets a push; the first answer decides. Nobody can approve their own access.";

export function SwitchRow({ label, desc, checked, onChange }) {
  return (
    <label style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start", cursor: "pointer" }}>
      <Switch size="small" checked={!!checked} onChange={onChange} style={{ marginTop: "0.125rem" }} aria-label={label} />
      <span style={{ display: "grid", gap: "0.125rem" }}>
        <span style={{ fontSize: "0.875rem", fontWeight: 600, color: AN_COLORS.ink }}>{label}</span>
        <Muted>{desc}</Muted>
      </span>
    </label>
  );
}

function Minutes({ id, label, help, value, onChange, onBlur, error }) {
  return (
    <Field label={label} help={help} error={error} htmlFor={id}>
      <span id={fieldDomId(id)} style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", scrollMarginTop: "1rem" }}>
        <InputNumber
          id={id}
          min={0}
          precision={0}
          value={value}
          onChange={(v) => onChange(v ?? 0)}
          onBlur={onBlur}
          status={error ? "error" : undefined}
          style={{ width: "7rem" }}
          aria-label={label}
        />
        <Muted>min</Muted>
      </span>
    </Field>
  );
}

function StoredRow({ children, onRemove, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8125rem", color: AN_COLORS.muted }}>
      <span style={{ flex: 1 }}>{children}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        style={{ border: "none", background: "transparent", cursor: "pointer", color: AN_COLORS.muted, display: "inline-flex" }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function ApprovalSection({ adapter, draft, patchDraft, errors, touch, data }) {
  if (draft.action === "block") return null;
  const a = draft.approval || {};
  const set = (fields) => patchDraft({ approval: { ...a, ...fields } });
  const subject = adapter.labels?.subject || "session";
  const cfg = adapter.features?.approval || {};
  const search = cfg.search;
  return (
    <Group id="policy-section-approval" title="Approval">
      <SwitchRow label="Require approval from someone else" desc={cfg.desc || APPROVAL_DESC} checked={a.required} onChange={(v) => set({ required: v })} />
      {a.required && (
        <>
          <Field label="Approvers" error={errors["approval.approvers"]} htmlFor="pf-approval-approvers">
            <div id={fieldDomId("approval.approvers")} style={{ scrollMarginTop: "1rem" }}>
              <RemotePicker
                id="pf-approval-approvers"
                ariaLabel="Approvers"
                sources={[{ key: "approvers", label: "Console users", search: (q) => (search ? search(q, { draft, data }) : Promise.resolve([])) }]}
                value={a.approvers || []}
                onChange={(v) => set({ approvers: v })}
                onBlur={() => touch("approval.approvers")}
                placeholder="Search console users"
                status={errors["approval.approvers"] ? "error" : undefined}
              />
            </div>
          </Field>
          {cfg.note && <Muted>{cfg.note}</Muted>}
          <Minutes
            id="approval.validMinutes"
            label={cfg.validLabel || "Approval lasts"}
            help={`0 = this ${subject} only`}
            value={a.validMinutes}
            onChange={(v) => set({ validMinutes: v })}
            onBlur={() => touch("approval.validMinutes")}
            error={errors["approval.validMinutes"]}
          />
          {(a.approverGroups || []).length > 0 && (
            <StoredRow label="approver groups" onRemove={() => set({ approverGroups: [] })}>
              Approver groups: {a.approverGroups.join(", ")}, {SET_OUTSIDE}
            </StoredRow>
          )}
          {a.requireJustification && (
            <StoredRow label="reason" onRemove={() => set({ requireJustification: false })}>
              Asks for a reason, {SET_OUTSIDE}
            </StoredRow>
          )}
          {a.requireTicket && (
            <StoredRow label="ticket" onRemove={() => set({ requireTicket: false })}>
              Asks for a ticket, {SET_OUTSIDE}
            </StoredRow>
          )}
        </>
      )}
    </Group>
  );
}

export function SessionSection({ adapter, draft, patchDraft, errors, touch }) {
  const s = draft.session || {};
  const controls = sessionControlsOf(adapter?.features?.session);
  const note = adapter?.features?.sessionNote ? adapter.features.sessionNote(draft) : "";
  const set = (fields) => patchDraft({ session: { ...s, ...fields } });
  const num = (key, label, help) => (
    <Minutes
      id={`session.${key}`}
      label={label}
      help={help}
      value={s[key]}
      onChange={(v) => set({ [key]: v })}
      onBlur={() => touch(`session.${key}`)}
      error={errors[`session.${key}`]}
    />
  );
  return (
    <Group id="policy-section-session" title="Session">
      {controls.record && (
        <SwitchRow label="Record session" desc="The session is refused if it can't be recorded." checked={s.record} onChange={(v) => set({ record: v })} />
      )}
      {note && <Note tone="warn">{note}</Note>}
      <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
        {num("maxDurationMinutes", "Maximum length", "0 = no limit")}
        {num("idleTimeoutMinutes", "Idle timeout", "0 = no limit, counts the user's input only")}
        {controls.reauth && num("reauthIntervalMinutes", "Re-verify every", "Ends the session; the user signs in again")}
      </div>
    </Group>
  );
}
